import { useState } from 'react';
import { Button, ConfigProvider, Dropdown, Flex, Space, Spin, theme, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const NO_ACTIONS_KEY = 'noActions';

/** Filtra ítems visibles y maneja el loading por ítem. */
const buildDropdownMenuItems = (items) => {
  const visibleItems = items.filter((item) => item.visible !== false);

  if (visibleItems.length === 0) {
    return {
      menuItems: [
        { key: NO_ACTIONS_KEY, label: 'No hay acciones disponibles', disabled: true },
      ],
      loadingKeys: [],
    };
  }

  const loadingKeys = []

  // Se extrae `visible` para que no se pase a los items finales del menú y así evitar warnings
  const menuItems = visibleItems.map(({
    label,
    icon,
    onClick,
    visible,
    loading,
    disabled,
    tooltip,
    disabledTooltip,
    style,
    ...rest
  }) => {
    if (loading) {
      loadingKeys.push(rest.key);
    }

    const tooltipTitle = loading
      ? null
      : disabled
        ? disabledTooltip
        : tooltip;

    const displayIcon = loading
      ? <Spin size='small' indicator={<LoadingOutlined />} style={{ color: 'inherit' }} />
      : icon;

    const menuLabel = tooltipTitle
      ? (
        <Tooltip placement='left' title={tooltipTitle}>
          <Space style={{ width: '100%' }}>
            {displayIcon}
            {label}
          </Space>
        </Tooltip>
      )
      : label;

    return {
      ...rest,
      disabled,
      label: menuLabel,
      ...(tooltipTitle ? {} : { icon: displayIcon }),
      onClick: loading ? undefined : onClick,
      style: {
        userSelect: 'none', // No permite seleccionar el texto
        ...style,
        ...(loading && { opacity: 0.65, cursor: 'default' }),
      },
    };
  });

  return { menuItems, loadingKeys };
};

/**
 * Dropdown de acciones.
 * Modo compacto: solo se muestra el botón del menú.
 * Modo extendido: muestra la primera acción como enlace junto al botón del menú.
 *
 * @param {Object} props
 * @param {Array<{
 *  key: string,
 *  label: React.ReactNode,
 *  icon?: React.ReactNode,
 *  onClick?: function,
 *  visible?: boolean,
 *  loading?: boolean,
 *  disabled?: boolean,
 *  danger?: boolean,
 *  tooltip?: React.ReactNode,
 *  disabledTooltip?: React.ReactNode,
 * }>} [props.items=[]] - Ítems del menú (compatible con Menu de Ant Design)
 * @param {boolean} [props.compact=true] - Controla el modo de visualización: compacto o extendido.
 * @param {import('antd').ButtonProps} [props.buttonProps] - Props del botón del menú.
 */
export const ActionsDropdown = ({ items = [], compact = true, buttonProps }) => {
  const [open, setOpen] = useState(false);
  const { token: themeToken } = theme.useToken();

  const { menuItems, loadingKeys } = buildDropdownMenuItems(items);
  const isAnyItemLoading = loadingKeys.length > 0;

  const firstItem = menuItems[0];
  const showFirstActionAsLink = !compact && firstItem?.key !== NO_ACTIONS_KEY;

  // Evita que se cierre el menú al hacer clic en un item cargando
  const handleMenuClick = ({ key }) => {
    if (loadingKeys.includes(key)) return;
    setOpen(false);
  };

  // Maneja la apertura/cierre del menú de manera controlada junto con handleMenuClick (tomado de la documentación de AntD)
  const handleOpenChange = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  };

  return (
    <Flex justify={compact ? 'center' : 'space-between'} align='center' gap='small'>
      {showFirstActionAsLink && (
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorLink: themeToken.colorPrimary,
                algorithm: true,
              },
            },
          }}
        >
          <Button
            type='link'
            onClick={firstItem.onClick}
            disabled={firstItem.disabled}
            danger={firstItem.danger}
            style={{ padding: 0 }}
          >
            {firstItem.label}
          </Button>
        </ConfigProvider>
      )}
      <Dropdown
        open={open}
        onOpenChange={handleOpenChange}
        trigger={['hover', 'click']}
        mouseEnterDelay={0.1}
        mouseLeaveDelay={0.3}
        menu={{ items: menuItems, onClick: handleMenuClick }}
        placement='bottomRight'
      >
        <Button
          color='primary'
          variant='text'
          shape='circle'
          icon={<FontAwesomeIcon icon={faEllipsisV} />}
          {...buttonProps}
          style={{
            ...buttonProps?.style,
            // Mantiene la alineación a la derecha cuando no hay acción primaria y compact=false
            marginLeft: !showFirstActionAsLink && !compact ? 'auto' : undefined,
          }}
          loading={isAnyItemLoading}
        />
      </Dropdown>
    </Flex>
  );
};
